<script setup lang="ts">
import { computed } from 'vue';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import TrackMatchingColumnMapping from '@/components/TrackMatchingColumnMapping.vue';
import { readSourceArtistName } from '@/trackMatching/artistRenameSource';
import { SOURCE_TYPES, useTrackMatchingStore } from '@/stores/trackMatchingStore';

/**
 * SAU-184 artist clean-up cell. On a Raw DDEX / Spotify match row the artist name becomes a button:
 * clicking it asks the page to queue a rename of the live track's artist to that cleaner name. Live
 * track rows (and source rows with nothing to clean) fall back to the read-only column mapping, which
 * also renders the queued-state highlight on the live track's own artist cell.
 */
interface ArtistCellRow extends Record<string, unknown> {
  live_track_id: string | number;
  source: string;
  id: string | number;
}

const props = defineProps<{
  row: ArtistCellRow;
  field: string;
  dataType: string;
  isLiveTrackProcessing?: boolean;
}>();

const emit = defineEmits<{
  (
    e: 'requestRename',
    payload: { liveTrackId: string | number; sourceId: string | number; source: string; name: string }
  ): void;
}>();

const store = useTrackMatchingStore();

const isSourceRow = computed(
  () => props.row.source === SOURCE_TYPES.SPOTIFY || props.row.source === SOURCE_TYPES.RAW_DDEX
);

const sourceArtistName = computed(() => readSourceArtistName(props.row));

/**
 * The rename works in place via live_track.artist_id, so a live track with no artist has nothing to
 * clean — fall back to the read-only mapping rather than offer a no-op click.
 */
const liveTrackHasArtist = computed(() => {
  const matchData = store.getLiveTrackRow(props.row.live_track_id).matchData as
    | Record<string, unknown>
    | undefined;
  const nested = matchData?.artist as { id?: unknown } | undefined;
  return (matchData?.artist_id ?? nested?.id ?? null) != null;
});

const canRename = computed(
  () => isSourceRow.value && sourceArtistName.value !== null && liveTrackHasArtist.value
);

const isQueued = computed(() =>
  store.isArtistRenameQueuedForSource(props.row.live_track_id, props.row.id)
);

function onClick() {
  if (!canRename.value || sourceArtistName.value === null) {
    return;
  }
  emit('requestRename', {
    liveTrackId: props.row.live_track_id,
    sourceId: props.row.id,
    source: props.row.source,
    name: sourceArtistName.value,
  });
}
</script>

<template>
  <Tooltip v-if="canRename">
    <TooltipTrigger as-child>
      <Button
        type="button"
        size="sm"
        :variant="isQueued ? 'accent' : 'ghost'"
        :disabled="isLiveTrackProcessing"
        :aria-label="
          isQueued
            ? `Artist clean-up queued: ${sourceArtistName}`
            : `Clean live track artist to ${sourceArtistName}`
        "
        data-testid="track-matching-artist-clean"
        class="h-auto w-full justify-start whitespace-normal px-1 py-0.5 text-left font-normal"
        @click="onClick"
      >
        {{ sourceArtistName }}
      </Button>
    </TooltipTrigger>
    <TooltipContent>
      <p>
        {{ isQueued ? 'Queued — click to undo' : 'Clean live track artist to this name' }}
      </p>
    </TooltipContent>
  </Tooltip>
  <TrackMatchingColumnMapping v-else :field="field" :dataType="dataType" :data="row" />
</template>
